import { useEffect, useRef, useState } from 'react'
import {
  Modal,
  Pressable,
  StyleSheet,
  type View,
  useWindowDimensions,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { SvgXml } from 'react-native-svg'

import { FigmaCloseIcon, FigmaInfoCircleIcon } from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

/** Where the trigger sits on screen: the arrow points at `x`, the bubble hangs off `top` or `bottom`. */
interface Anchor {
  x: number
  top: number
  bottom: number
}

interface Placement {
  side: 'above' | 'below'
  /** Screen-space `top` of the box the bubble grows out of. */
  top: number
}

const ARROW_HEIGHT = 13
const ARROW_GAP = 8
const EDGE_MARGIN = 8

/**
 * Above the trigger by default, as the Figma tip is drawn; below it when a tall
 * body (translated copy, large text, a short landscape window) would run off
 * the top. Pinned to the top edge when neither side has room.
 */
export function placeTooltip({
  anchor,
  bodyHeight,
  topEdge,
  bottomEdge,
}: {
  anchor: Anchor
  bodyHeight: number
  topEdge: number
  bottomEdge: number
}): Placement {
  const above = anchor.top - ARROW_GAP - ARROW_HEIGHT
  if (above - bodyHeight >= topEdge) {
    return { side: 'above', top: above }
  }
  const below = anchor.bottom + ARROW_GAP + ARROW_HEIGHT
  if (below + bodyHeight <= bottomEdge) {
    return { side: 'below', top: below }
  }
  return { side: 'above', top: Math.max(above, topEdge + bodyHeight) }
}

export function InfoTooltip({
  title,
  description,
}: {
  title: string
  description: string
}) {
  const theme = useTheme()
  const { t } = useFunkitTranslation()
  const { width, height } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const trigger = useRef<View>(null)
  const [anchor, setAnchor] = useState<Anchor>()
  const [bodyHeight, setBodyHeight] = useState(0)
  const close = () => {
    setAnchor(undefined)
    setBodyHeight(0)
  }
  const left = Math.max(16, (anchor?.x ?? 0) - 16.5)
  const bubbleWidth = Math.min(293, width - left - 16)
  const backgroundColor = theme.background.button.secondary.pressed
  const placement = anchor
    ? placeTooltip({
        anchor,
        bodyHeight,
        topEdge: insets.top + EDGE_MARGIN,
        bottomEdge: height - insets.bottom - EDGE_MARGIN,
      })
    : undefined
  const isBelow = placement?.side === 'below'

  useEffect(() => {
    setAnchor(undefined)
    setBodyHeight(0)
  }, [width, height])

  return (
    <>
      <Pressable
        ref={trigger}
        accessibilityRole="button"
        accessibilityLabel={title}
        accessibilityState={{ expanded: !!anchor }}
        hitSlop={14}
        onPress={() =>
          trigger.current?.measure((_x, _y, w, h, pageX, pageY) =>
            setAnchor({ x: pageX + w / 2, top: pageY, bottom: pageY + h }),
          )
        }
      >
        <FigmaInfoCircleIcon
          size={16}
          color={theme.content.elements.secondary}
        />
      </Pressable>
      <Modal
        transparent
        visible={!!anchor}
        statusBarTranslucent
        navigationBarTranslucent
        supportedOrientations={['portrait', 'landscape']}
        onRequestClose={close}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={close}
          accessible={false}
        />
        {placement ? (
          <Box
            style={{
              position: 'absolute',
              top: placement.top,
              left,
              width: bubbleWidth,
            }}
          >
            <Box
              accessibilityViewIsModal
              onAccessibilityEscape={close}
              onLayout={({ nativeEvent }) =>
                setBodyHeight(nativeEvent.layout.height)
              }
              style={[
                styles.bubble,
                isBelow ? styles.bubbleBelow : styles.bubbleAbove,
                {
                  width: '100%',
                  opacity: bodyHeight > 0 ? 1 : 0,
                },
              ]}
            >
              {/* The arrow is drawn hanging off the bottom; below the trigger
                  the whole outline is mirrored so it leads from the top. */}
              <SvgXml
                pointerEvents="none"
                width={bubbleWidth}
                height={bodyHeight + ARROW_HEIGHT}
                style={[
                  StyleSheet.absoluteFill,
                  isBelow ? styles.arrowUp : undefined,
                ]}
                xml={`<svg xmlns="http://www.w3.org/2000/svg" width="${bubbleWidth}" height="${bodyHeight + ARROW_HEIGHT}"><path fill="${backgroundColor}" d="${tooltipShape(bubbleWidth, bodyHeight)}" /></svg>`}
              />
              <Box flexDirection="row" alignItems="center">
                <Text
                  variant="bodyMedium"
                  accessibilityRole="header"
                  style={styles.title}
                >
                  {title}
                </Text>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('common.close')}
                  onPress={close}
                  hitSlop={10}
                  style={[
                    styles.close,
                    { backgroundColor: theme.content.elements.ghosted },
                  ]}
                >
                  <FigmaCloseIcon
                    size={14}
                    color={theme.content.elements.secondary}
                  />
                </Pressable>
              </Box>
              <Text
                variant="body"
                style={{ color: theme.content.elements.secondary }}
              >
                {description}
              </Text>
            </Box>
          </Box>
        ) : null}
      </Modal>
    </>
  )
}

const styles = StyleSheet.create({
  bubble: { position: 'absolute', padding: 16 },
  bubbleAbove: { bottom: 0 },
  bubbleBelow: { top: 0 },
  arrowUp: { top: -ARROW_HEIGHT, transform: [{ scaleY: -1 }] },
  title: { flex: 1 },
  close: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
})

// Figma iOS Mini Tip's down-leading arrow is part of the background outline.
function tooltipShape(width: number, height: number) {
  return `M26 0 H${width - 26} Q${width} 0 ${width} 26
    V${height - 26} Q${width} ${height} ${width - 26} ${height} H40
    V${height + 0.003}
    C38.2876 ${height + 0.003} 36.5757 ${height - 0.005} 34.8633 ${height + 0.014}
    C33.2244 ${height + 0.032} 31.3899 ${height + 0.057} 29.7949 ${height + 0.658}
    C28.0719 ${height + 1.308} 26.9309 ${height + 2.445} 25.7832 ${height + 3.782}
    C24.9569 ${height + 4.744} 23.3342 ${height + 6.797} 22.5439 ${height + 7.788}
    C21.8971 ${height + 8.601} 20.6308 ${height + 10.206} 19.9395 ${height + 10.983}
    C19.0692 ${height + 11.961} 17.9931 ${height + 13} 16.499 ${height + 13}
    C15.0052 ${height + 13} 13.9302 ${height + 11.961} 13.0605 ${height + 10.984}
    C12.3693 ${height + 10.208} 11.1034 ${height + 8.601} 10.4561 ${height + 7.789}
    C9.66679 ${height + 6.798} 8.04365 ${height + 4.745} 7.2168 ${height + 3.783}
    C1.0535 ${height - 3.387} 0.153786 ${height - 4.434} 0.0224609 ${height - 11.991}
    C0.0194123 ${height - 12.15} 0.0169193 ${height - 12.311} 0.0146484 ${height - 12.476}
    C0.0129439 ${height - 12.608} 0.0120848 ${height - 12.742} 0.0107422 ${height - 12.878}
    C0.00122687 ${height - 13.812} 0 ${height - 14.847} 0 ${height - 16}
    V26 Q0 0 26 0 Z`
}
